import type { ActivityType } from '@baby-tracker/contracts';
import { activityTypeSchema } from '@baby-tracker/contracts';
import {
    calendarDateInZone,
    calendarDateSchema,
    entityIdSchema,
    familyRoleAtLeast,
    zonedDayBounds,
} from '@baby-tracker/validation';
import { History } from 'lucide-react';
import { useState, type ReactElement } from 'react';
import { useSearchParams } from 'react-router';

import { HistoryList } from '../components/features/tracking/HistoryList.js';
import { MonthCalendar } from '../components/features/tracking/MonthCalendar.js';
import styles from '../components/features/tracking/tracking.module.css';
import { PageHeader } from '../components/layout/PageHeader.js';
import { Button } from '../components/ui/Button.js';
import { EmptyState } from '../components/ui/EmptyState.js';
import { errorText } from '../components/ui/error-text.js';
import { ErrorState } from '../components/ui/ErrorState.js';
import fieldStyles from '../components/ui/fields.module.css';
import { SelectField } from '../components/ui/SelectField.js';
import { Spinner } from '../components/ui/Spinner.js';
import { useActiveFamily } from '../hooks/useActiveFamily.js';
import { useChildren } from '../hooks/useChildren.js';
import { useFamily } from '../hooks/useFamily.js';
import { useDayRollups, useTimeline } from '../hooks/useTracking.js';
import { recentWindow } from '../lib/calendar.js';
import { formatCalendarDate } from '../lib/labels.js';

const typeOptions = [
    { value: '', label: 'All activity' },
    { value: 'feeding', label: 'Feeds' },
    { value: 'pumping', label: 'Pumping' },
    { value: 'sleep', label: 'Sleep' },
    { value: 'nappy', label: 'Nappies' },
] as const;

export function HistoryPage(): ReactElement {
    const { active, me } = useActiveFamily();
    const family = useFamily(active?.id);
    const children = useChildren(active?.id, true);
    const [params, setParams] = useSearchParams();
    const [monthOverride, setMonthOverride] = useState<string | null>(null);
    const today = family.data ? calendarDateInZone(new Date(), family.data.timeZone) : undefined;
    const requestedDate = calendarDateSchema.safeParse(params.get('date'));
    const selectedDate = requestedDate.success ? requestedDate.data : undefined;
    const requestedChild = entityIdSchema.safeParse(params.get('child'));
    const childId = requestedChild.success ? requestedChild.data : undefined;
    const requestedType = activityTypeSchema.safeParse(params.get('type'));
    const activityType: ActivityType | undefined = requestedType.success
        ? requestedType.data
        : undefined;
    const month = monthOverride ?? (selectedDate ?? today ?? '2026-01').slice(0, 7);
    const monthStart = `${month}-01`;
    const monthEnd = lastDayOfMonth(monthStart);
    const rollups = useDayRollups(
        calendarDateSchema.safeParse(monthStart).success ? active?.id : undefined,
        calendarDateSchema.safeParse(monthStart).success ? monthStart : undefined,
        calendarDateSchema.safeParse(monthEnd).success ? monthEnd : undefined,
    );
    const window =
        family.data === undefined
            ? undefined
            : selectedDate === undefined
              ? recentWindow(family.data.timeZone, 7)
              : zonedDayBounds(selectedDate, family.data.timeZone);
    const timeline = useTimeline(active?.id, window?.from, window?.to, {
        ...(childId === undefined ? {} : { childId }),
        ...(activityType === undefined ? {} : { type: activityType }),
    });
    const canLog = active !== undefined && familyRoleAtLeast(active.role, 'caregiver');
    const loadError = family.error ?? children.error ?? timeline.error;
    const loading = me.isPending || family.isLoading || children.isLoading || timeline.isLoading;
    const childOptions = [
        { value: '', label: 'All children' },
        ...(children.data?.map((child) => ({ value: child.id, label: child.name })) ?? []),
    ];

    function setParam(key: string, value: string): void {
        const next = new URLSearchParams(params);
        if (value.length === 0) {
            next.delete(key);
        } else {
            next.set(key, value);
        }
        setParams(next, { replace: true });
    }

    return (
        <>
            <PageHeader
                title="History"
                subtitle={selectedDate ? formatCalendarDate(selectedDate) : 'Last 7 days'}
            />
            <div className={styles.columns}>
                <MonthCalendar
                    month={month}
                    selected={selectedDate}
                    days={rollups.data}
                    onMonth={setMonthOverride}
                    onSelect={(date) => {
                        setParam('date', date);
                    }}
                />
                <div className={fieldStyles.stack}>
                    <SelectField
                        label="Child"
                        options={childOptions}
                        value={childId ?? ''}
                        onChange={(event) => {
                            setParam('child', event.target.value);
                        }}
                    />
                    <SelectField
                        label="Activity"
                        options={typeOptions}
                        value={activityType ?? ''}
                        onChange={(event) => {
                            setParam('type', event.target.value);
                        }}
                    />
                    {selectedDate === undefined ? null : (
                        <Button
                            variant="ghost"
                            onClick={() => {
                                setParam('date', '');
                            }}
                        >
                            Show the last 7 days
                        </Button>
                    )}
                </div>
            </div>
            {loading ? <Spinner label="Loading history" /> : null}
            {loadError ? (
                <ErrorState title="Could not load history" description={errorText(loadError)} />
            ) : null}
            {timeline.data && timeline.data.length >= 100 ? (
                <p className={styles.summary}>
                    Showing the latest 100 records. Narrow the day or the filters.
                </p>
            ) : null}
            {timeline.data?.length === 0 ? (
                <EmptyState
                    icon={History}
                    title="Nothing in this view"
                    description="Try another day, child, or activity type."
                />
            ) : null}
            {timeline.data && timeline.data.length > 0 && family.data ? (
                <HistoryList
                    familyId={family.data.id}
                    items={timeline.data}
                    children={children.data ?? []}
                    volumeUnit={family.data.unitVolume}
                    massUnit={family.data.unitMass}
                    timeZone={family.data.timeZone}
                    canLog={canLog}
                />
            ) : null}
        </>
    );
}

function lastDayOfMonth(monthStart: string): string {
    const [year, month] = monthStart.split('-').map((part) => Number(part));
    const last = new Date(Date.UTC(year ?? 2026, month ?? 1, 0));
    return last.toISOString().slice(0, 10);
}
